import { useEffect, useRef } from 'react';
import { apiClient, type ApiEnvelope } from '@/shared/api/client';
import { toast } from '@/components/ui/Toast';
import { useSipStore, type SipCredentials } from './sipStore';
import type { SipCredentialsResponse } from '@/shared/api/agentApi';
import { releaseSipLineOnUnload } from './sipDeviceClaim';
import { signalDialerExtension, startDialerExtensionHeartbeat } from '@/shared/lib/dialerExtensionBridge';

const SOFT_MODE = import.meta.env.VITE_SIP_SOFT === '1';

/** Which persona base path serves sip-credentials for the logged-in user. */
type Persona = 'agent' | 'admin' | 'super';

function sipEndpoint(persona: Persona): string {
  if (persona === 'admin') return '/admin/sip-credentials';
  if (persona === 'super') return '/super/sip-credentials';
  return '/agent/sip-credentials';
}

function mapCreds(c: SipCredentialsResponse): SipCredentials {
  return {
    wsServer: c.ws_url,
    uri: `sip:${c.sip_uri}`,
    password: c.sip_password,
    displayName: c.display_name,
    domain: c.sip_domain,
    iceServers: c.ice_servers,
    defaultCallerDid: c.default_caller_did,
    dids: c.dids,
  };
}

/**
 * Registers the WebRTC softphone line for ANY persona that has an extension
 * (agent / admin / supervisor / super-admin). The backend resolver is
 * persona-agnostic — it hands out SIP creds whenever the user is linked to an
 * extension — so admins with a phone get the same live softphone as agents.
 *
 * A user WITHOUT an extension gets a clean 422 → we register in "no line" mode
 * so the softphone simply shows "no extension" instead of erroring. This makes
 * the softphone safe to mount in every layout.
 */
/**
 * Backoff between automatic re-registration attempts after a failure.
 * Deliberately slow: JsSIP already retries the WebSocket on its own, and a
 * pile of UAs hammering /ws once took ARI down (17/08). One fresh attempt
 * every 15s → 2min is enough to recover from a server restart without
 * turning a dead server into a self-inflicted flood.
 */
const RETRY_DELAYS_MS = [15_000, 30_000, 60_000, 120_000];

export function useSipRegistration(persona: Persona): void {
  const register = useSipStore((s) => s.register);
  const registered = useSipStore((s) => s.registered);
  const lineDisabled = useSipStore((s) => s.lineDisabled);
  const reconnectNonce = useSipStore((s) => s.reconnectNonce);
  const state = useSipStore((s) => s.state);
  const noLine = useSipStore((s) => s.noLine);
  const otherTab = useSipStore((s) => s.otherTab);
  const otherDevice = useSipStore((s) => s.otherDevice);
  const attempt = useRef(0);

  // גשר לתוסף ה-Chrome: כשהקו כאן רשום, מודיעים לתוסף (עם heartbeat) כדי שישהה
  // את הסופטפון שלו ולא יירשם אותו קו פעמיים. כשמתנתק — מודיעים שאינו רשום.
  // beforeunload מבטיח שהתוסף יתעורר מחדש גם בסגירת טאב לא-נקייה (רענון/סגירה).
  useEffect(() => {
    if (registered) {
      const stop = startDialerExtensionHeartbeat();
      return stop;
    }
    signalDialerExtension(false);
  }, [registered]);

  useEffect(() => {
    const onUnload = () => {
      signalDialerExtension(false);
      // משחררים את הקו בשרת רק אם הוא אצלנו — מכשיר אחר לא יחכה 75ש.
      if (useSipStore.getState().registered) releaseSipLineOnUnload();
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  // רישום שנכשל (registrationFailed, 8 ניתוקים, כשל במשיכת creds) השאיר את
  // הקו תקוע עד רענון: ה-effect שלמטה רץ רק כש-`registered` נופל, ורישום
  // שמעולם לא הצליח לא מפיל כלום. כאן מתזמנים ניסיון נוסף עם backoff, דרך
  // אותו מונה ש-dial() משתמש בו. נעצר ברגע שהקו נרשם, כשהמשתמש ניתק
  // במפורש, או כשאין לו שלוחה בכלל.
  useEffect(() => {
    if (registered) { attempt.current = 0; return; }
    // otherTab: הקו חי בלשונית אחרת — sipTabLock יעביר אותו לכאן כשהיא תיסגר.
    if (state !== 'error' || lineDisabled || noLine || otherTab || otherDevice) return;

    const delay = RETRY_DELAYS_MS[Math.min(attempt.current, RETRY_DELAYS_MS.length - 1)];
    attempt.current += 1;
    const timer = window.setTimeout(() => {
      const st = useSipStore.getState();
      if (st.registered || st.lineDisabled || st.noLine || st.otherTab || st.otherDevice) return;
      console.info('[SIP] scheduled re-registration attempt');
      useSipStore.setState({ reconnectNonce: st.reconnectNonce + 1 });
    }, delay);

    return () => window.clearTimeout(timer);
  }, [state, registered, lineDisabled, noLine, otherTab, otherDevice]);

  useEffect(() => {
    // "מנותק" מפורש — לא רושמים מחדש אוטומטית, אחרת הניתוק מתבטל מיידית.
    if (registered || lineDisabled) return;
    let cancelled = false;

    void (async () => {
      try {
        const { data } = await apiClient.get<ApiEnvelope<SipCredentialsResponse>>(sipEndpoint(persona));
        if (cancelled) return;
        const creds = data.data ?? null;
        if (creds) {
          await register(mapCreds(creds));
        } else {
          // No extension for this user — soft-register so the UI degrades
          // gracefully (no toast; a manager without a phone is normal).
          await register(null);
        }
      } catch (e) {
        if (cancelled) return;
        // 422 = no extension provisioned → soft mode, no scary toast.
        const status = (e as { response?: { status?: number } })?.response?.status;
        if (status === 422 || SOFT_MODE) {
          await register(null);
        } else {
          useSipStore.setState({ state: 'error', error: 'SIP credentials failed' });
          toast('שגיאה בחיבור הקו (SIP)', 'danger');
        }
      }
    })();

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registered, lineDisabled, persona, reconnectNonce]);
}
